import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  BoltIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  digitalSignaturesApi,
  DigitalSignatureDependenciesResponse,
  DigitalSignatureIntegrationStatus,
  DigitalSignatureOverview,
  DigitalSignatureValidationResponse,
  SignatureCertificateProfile,
  SignaturePolicyProfile,
} from './digitalSignaturesApi';

const defaultPolicy = (): SignaturePolicyProfile => ({
  profile_key: `policy-${Date.now()}`,
  display_name: 'New Signature Policy',
  description: '',
  signature_mode: 'standard',
  require_flatten: true,
  require_evidence: true,
  require_crypto_signature: false,
  allow_drawn_signature: true,
  allow_typed_signature: true,
  allow_uploaded_signature_image: true,
  is_default: false,
  is_active: true,
  policy_json: null,
});

const defaultCertificate = (): SignatureCertificateProfile => ({
  alias: `cert-${Date.now()}`,
  provider: 'local',
  mode: 'test',
  external_key_ref: '',
  display_name: '',
  subject_cn: '',
  issuer_cn: '',
  expires_at: null,
  is_active: true,
  metadata_json: null,
});

const asBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  return fallback;
};

const statusBadge = (status: DigitalSignatureIntegrationStatus) => {
  if (status === 'ready') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
  }
  if (status === 'warnings') {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
  }
  return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
};

const statusLabel = (status: DigitalSignatureIntegrationStatus): string => {
  if (status === 'ready') return 'Complete';
  if (status === 'warnings') return 'Action Required';
  return 'Blocked';
};

const DigitalSignaturesTab: React.FC = () => {
  const [overview, setOverview] = useState<DigitalSignatureOverview | null>(null);
  const [dependencies, setDependencies] = useState<DigitalSignatureDependenciesResponse | null>(null);
  const [policies, setPolicies] = useState<SignaturePolicyProfile[]>([]);
  const [certificates, setCertificates] = useState<SignatureCertificateProfile[]>([]);
  const [validationResult, setValidationResult] = useState<DigitalSignatureValidationResponse | null>(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string>('');
  const [selectedCertificateAlias, setSelectedCertificateAlias] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);
  const [savingPolicies, setSavingPolicies] = useState<boolean>(false);
  const [savingCertificates, setSavingCertificates] = useState<boolean>(false);
  const [runningValidation, setRunningValidation] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, dependencyData, policyData, certificateData] = await Promise.all([
        digitalSignaturesApi.getOverview(),
        digitalSignaturesApi.getDependencies(),
        digitalSignaturesApi.getPolicies(),
        digitalSignaturesApi.getCertificates(),
      ]);

      setOverview(overviewData);
      setDependencies(dependencyData);
      setPolicies(policyData || []);
      setCertificates(certificateData || []);

      const defaultPolicyId =
        policyData.find((p) => p.is_default)?.profile_id ||
        policyData[0]?.profile_id ||
        '';
      setSelectedPolicyId(defaultPolicyId);

      const defaultCertAlias = certificateData[0]?.alias || '';
      setSelectedCertificateAlias(defaultCertAlias);
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load digital signature integration data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const canSavePolicies = useMemo(() => {
    return policies.every((policy) => policy.profile_key?.trim() && policy.display_name?.trim());
  }, [policies]);

  const canSaveCertificates = useMemo(() => {
    return certificates.every((certificate) => certificate.alias?.trim());
  }, [certificates]);

  const updatePolicy = (index: number, patch: Partial<SignaturePolicyProfile>) => {
    setPolicies((current) => {
      const next = [...current];
      next[index] = { ...next[index], ...patch };
      if (patch.is_default) {
        return next.map((policy, policyIndex) => ({
          ...policy,
          is_default: policyIndex === index,
        }));
      }
      return next;
    });
  };

  const updateCertificate = (index: number, patch: Partial<SignatureCertificateProfile>) => {
    setCertificates((current) => {
      const next = [...current];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const savePolicies = async () => {
    if (!canSavePolicies) {
      setError('Every policy must include a profile key and display name.');
      return;
    }
    setSavingPolicies(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await digitalSignaturesApi.savePolicies(policies);
      setPolicies(saved || []);
      setMessage('Policy profiles saved.');
    } catch (saveError: any) {
      setError(saveError?.message || 'Failed to save policy profiles.');
    } finally {
      setSavingPolicies(false);
    }
  };

  const saveCertificates = async () => {
    if (!canSaveCertificates) {
      setError('Every certificate row must include an alias.');
      return;
    }
    setSavingCertificates(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await digitalSignaturesApi.saveCertificates(certificates);
      setCertificates(saved || []);
      setMessage('Certificate profiles saved.');
    } catch (saveError: any) {
      setError(saveError?.message || 'Failed to save certificate profiles.');
    } finally {
      setSavingCertificates(false);
    }
  };

  const runValidation = async () => {
    setRunningValidation(true);
    setError(null);
    setMessage(null);
    try {
      const result = await digitalSignaturesApi.validateConfiguration({
        policy_profile_id: selectedPolicyId || undefined,
        certificate_alias: selectedCertificateAlias || undefined,
      });
      setValidationResult(result);
      setMessage('Validation completed.');
    } catch (validateError: any) {
      setError(validateError?.message || 'Validation failed.');
    } finally {
      setRunningValidation(false);
    }
  };

  const addPolicy = () => setPolicies((current) => [...current, defaultPolicy()]);
  const addCertificate = () => setCertificates((current) => [...current, defaultCertificate()]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-sm text-gray-600 dark:text-gray-300">
        Loading digital signature integrations...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <ShieldCheckIcon className="h-6 w-6 text-lex-accent mr-2" />
          Digital Signatures
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage signature policies, dependency readiness, certificate metadata, and signing validation.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {message && (
        <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 p-3 text-sm text-green-700 dark:text-green-300">
          {message}
        </div>
      )}

      {overview?.onboarding && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Onboarding Readiness</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {overview.onboarding.completed_checks}/{overview.onboarding.total_checks} setup checks complete
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(overview.onboarding.overall_status)}`}>
                {statusLabel(overview.onboarding.overall_status)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {overview.onboarding.completion_percent}% complete
              </span>
            </div>
          </div>
          <div className="px-4 py-3">
            <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${overview.onboarding.overall_status === 'ready'
                  ? 'bg-green-500'
                  : overview.onboarding.overall_status === 'warnings'
                    ? 'bg-amber-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${Math.max(0, Math.min(100, overview.onboarding.completion_percent))}%` }}
              />
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Attorney Signers</div>
                <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                  {overview.onboarding.lawyer_readiness.signer_ready}/{overview.onboarding.lawyer_readiness.attorney_total}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {overview.onboarding.lawyer_readiness.signer_pending} pending readiness
                </div>
              </div>
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 md:col-span-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Last Integration Check</div>
                <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                  {new Date(overview.checked_at).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Open each section below to clear blockers before production rollout.
                </div>
              </div>
            </div>
            {overview.onboarding.lawyer_readiness.pending_users.length > 0 && (
              <div className="mt-4 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 p-3">
                <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                  Signer Profiles Needing Attention ({overview.onboarding.lawyer_readiness.pending_users.length})
                </div>
                <div className="space-y-2">
                  {overview.onboarding.lawyer_readiness.pending_users.map((signer) => (
                    <div
                      key={signer.user_id}
                      className="rounded-md border border-amber-200/80 dark:border-amber-700/60 bg-white/70 dark:bg-gray-900/30 p-2"
                    >
                      <div className="text-xs font-medium text-gray-900 dark:text-white">
                        {signer.full_name}
                        <span className="ml-1 text-gray-500 dark:text-gray-400">({signer.role})</span>
                      </div>
                      {signer.email && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">{signer.email}</div>
                      )}
                      <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
                        {signer.missing_items.join(' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 space-y-2">
              {overview.onboarding.sections.map((section) => (
                <div
                  key={section.key}
                  className="rounded-md border border-gray-200 dark:border-gray-700 p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{section.title}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge(section.status)}`}>
                        {statusLabel(section.status)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{section.details}</div>
                  </div>
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {section.action_label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Policy Profiles</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{overview?.policies.total ?? 0}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{overview?.policies.active ?? 0} active</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Certificate Profiles</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{overview?.certificates.total ?? 0}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{overview?.certificates.active ?? 0} active</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Signature Sessions (24h)</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{overview?.sessions.committed_last_24h ?? 0}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{overview?.sessions.active ?? 0} active now</div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Dependency Health</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Runtime readiness checks for signing stack</p>
          </div>
          <button
            type="button"
            onClick={loadAll}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Refresh
          </button>
        </div>
        <div className="p-4 space-y-3">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(dependencies?.status || 'blockers')}`}>
            {dependencies?.status || 'blockers'}
          </span>
          <div className="space-y-2">
            {(dependencies?.checks || []).map((check) => (
              <div key={check.binary} className="flex items-start justify-between border border-gray-200 dark:border-gray-700 rounded-md p-3">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{check.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{check.notes}</div>
                  {check.version && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{check.version}</div>}
                </div>
                <div className="flex items-center">
                  {check.available ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{check.required ? 'Required' : 'Optional'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Policy Profiles</h4>
          <div className="space-x-2">
            <button
              type="button"
              onClick={addPolicy}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Policy
            </button>
            <button
              type="button"
              onClick={savePolicies}
              disabled={savingPolicies}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-lex-accent text-lex-accent-contrast disabled:opacity-50"
            >
              {savingPolicies ? 'Saving...' : 'Save Policies'}
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {policies.map((policy, index) => (
            <div key={policy.profile_id || `policy-${index}`} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Policy key"
                  value={policy.profile_key || ''}
                  onChange={(event) => updatePolicy(index, { profile_key: event.target.value })}
                />
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Display name"
                  value={policy.display_name || ''}
                  onChange={(event) => updatePolicy(index, { display_name: event.target.value })}
                />
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Mode (standard/acroform/overlay)"
                  value={policy.signature_mode || 'standard'}
                  onChange={(event) => updatePolicy(index, { signature_mode: event.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 text-xs text-gray-600 dark:text-gray-300">
                <label className="inline-flex items-center"><input type="checkbox" className="mr-2" checked={asBoolean(policy.require_flatten, true)} onChange={(event) => updatePolicy(index, { require_flatten: event.target.checked })} />Require flatten</label>
                <label className="inline-flex items-center"><input type="checkbox" className="mr-2" checked={asBoolean(policy.require_evidence, true)} onChange={(event) => updatePolicy(index, { require_evidence: event.target.checked })} />Require evidence</label>
                <label className="inline-flex items-center"><input type="checkbox" className="mr-2" checked={asBoolean(policy.require_crypto_signature, false)} onChange={(event) => updatePolicy(index, { require_crypto_signature: event.target.checked })} />Require crypto</label>
                <label className="inline-flex items-center"><input type="checkbox" className="mr-2" checked={asBoolean(policy.is_active, true)} onChange={(event) => updatePolicy(index, { is_active: event.target.checked })} />Active</label>
                <label className="inline-flex items-center"><input type="checkbox" className="mr-2" checked={asBoolean(policy.is_default, false)} onChange={(event) => updatePolicy(index, { is_default: event.target.checked })} />Default</label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Certificate Metadata</h4>
          <div className="space-x-2">
            <button
              type="button"
              onClick={addCertificate}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Certificate
            </button>
            <button
              type="button"
              onClick={saveCertificates}
              disabled={savingCertificates}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-lex-accent text-lex-accent-contrast disabled:opacity-50"
            >
              {savingCertificates ? 'Saving...' : 'Save Certificates'}
            </button>
          </div>
        </div>
        <div className="p-4 space-y-3">
          {certificates.map((certificate, index) => (
            <div key={certificate.certificate_id || `certificate-${index}`} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Alias"
                  value={certificate.alias || ''}
                  onChange={(event) => updateCertificate(index, { alias: event.target.value })}
                />
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Provider (local/hsm/vault)"
                  value={certificate.provider || 'local'}
                  onChange={(event) => updateCertificate(index, { provider: event.target.value })}
                />
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Mode (test/production)"
                  value={certificate.mode || 'test'}
                  onChange={(event) => updateCertificate(index, { mode: event.target.value })}
                />
                <input
                  type="date"
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  value={(certificate.expires_at || '').slice(0, 10)}
                  onChange={(event) => updateCertificate(index, { expires_at: event.target.value ? `${event.target.value}T00:00:00.000Z` : null })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Display name"
                  value={certificate.display_name || ''}
                  onChange={(event) => updateCertificate(index, { display_name: event.target.value })}
                />
                <input
                  className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Subject CN"
                  value={certificate.subject_cn || ''}
                  onChange={(event) => updateCertificate(index, { subject_cn: event.target.value })}
                />
                <label className="inline-flex items-center text-xs text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={asBoolean(certificate.is_active, true)}
                    onChange={(event) => updateCertificate(index, { is_active: event.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
            <BoltIcon className="h-4 w-4 mr-2 text-lex-accent" />
            Validation Lab
          </h4>
          <button
            type="button"
            onClick={runValidation}
            disabled={runningValidation}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-lex-accent text-lex-accent-contrast disabled:opacity-50"
          >
            {runningValidation ? 'Validating...' : 'Validate Configuration'}
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select
              value={selectedPolicyId}
              onChange={(event) => setSelectedPolicyId(event.target.value)}
              className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="">Select policy profile</option>
              {policies.map((policy) => (
                <option key={policy.profile_id || policy.profile_key} value={policy.profile_id || ''}>
                  {policy.display_name} ({policy.profile_key})
                </option>
              ))}
            </select>
            <select
              value={selectedCertificateAlias}
              onChange={(event) => setSelectedCertificateAlias(event.target.value)}
              className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="">Select certificate alias</option>
              {certificates.map((certificate) => (
                <option key={certificate.alias} value={certificate.alias}>
                  {certificate.alias}
                </option>
              ))}
            </select>
          </div>

          {validationResult && (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(validationResult.status)}`}>
                {validationResult.status}
              </span>
              <div className="mt-3 space-y-2">
                {validationResult.issues.length === 0 ? (
                  <div className="text-sm text-green-700 dark:text-green-300">No issues found. Configuration is ready.</div>
                ) : (
                  validationResult.issues.map((issue, index) => (
                    <div
                      key={`${issue.severity}-${index}`}
                      className={`text-sm px-3 py-2 rounded-md ${issue.severity === 'blocker'
                        ? 'bg-red-50 text-red-700 dark:bg-red-900/10 dark:text-red-300'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-900/10 dark:text-amber-300'
                      }`}
                    >
                      <span className="font-semibold uppercase text-xs mr-2">{issue.severity}</span>
                      {issue.message}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DigitalSignaturesTab;
